import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Droplets, Sparkles, Leaf, MapPin, Truck, Cloud, Wallet, Clock, Sun, Heart, Shield, Users } from 'lucide-react';
import { Button } from './ui/button';
import { useSection, resolveImage } from '../contexts/ContentContext';

const ICON_MAP = { Leaf, MapPin, Sparkles, Droplets, Truck, Cloud, Wallet, Clock, Sun, Heart, Shield, Users };

export default function Hero() {
  const home = useSection('home');
  const h = home.hero || {};

  return (
    <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-white to-emerald-50"></div>

      <div className="absolute top-20 -left-32 w-96 h-96 bg-sky-200/40 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 -right-32 w-[28rem] h-[28rem] bg-emerald-200/40 rounded-full blur-3xl"></div>

      <Droplets className="absolute top-32 right-[15%] w-8 h-8 text-sky-300/50 animate-float" />
      <Droplets className="absolute bottom-40 left-[10%] w-6 h-6 text-emerald-300/50 animate-float" style={{ animationDelay: '2s' }} />
      <Sparkles className="absolute top-1/2 right-[5%] w-5 h-5 text-sky-400/40 animate-float" style={{ animationDelay: '1s' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="text-center lg:text-left">
            {h.badge && (
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-sky-100 shadow-sm mb-6">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                <span className="text-xs font-semibold text-sky-700 tracking-wide">{h.badge}</span>
              </div>
            )}

            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] tracking-tight text-sky-950 mb-6">
              {h.titleLine1}
              <span className="block gradient-text">{h.titleLine2}</span>
              <span className="block text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-500 mt-3">{h.subtitle}</span>
            </h1>

            {h.description && (
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl mx-auto lg:mx-0 mb-8" dangerouslySetInnerHTML={{ __html: h.description }} />
            )}

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start mb-10">
              {h.primaryCtaLabel && (
                <Link to={h.primaryCtaLink || '/soutenir'}>
                  <Button size="lg" className="water-btn bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white text-base font-semibold px-7 py-6 rounded-2xl border-0 shadow-lg shadow-sky-200">
                    {h.primaryCtaLabel}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              )}
              {h.secondaryCtaLabel && (
                <Link to={h.secondaryCtaLink || '/premierkiosqueàeauàpau'}>
                  <Button size="lg" variant="outline" className="text-base font-semibold px-7 py-6 rounded-2xl border-2 border-sky-200 hover:bg-sky-50 hover:border-sky-300 text-sky-900">
                    {h.secondaryCtaLabel}
                  </Button>
                </Link>
              )}
            </div>

            {Array.isArray(h.trustBadges) && h.trustBadges.length > 0 && (
              <div className="flex flex-wrap items-center gap-6 justify-center lg:justify-start text-sm text-slate-600">
                {h.trustBadges.map((b, i) => {
                  const Icon = ICON_MAP[b.icon] || Leaf;
                  return (
                    <div key={i} className="flex items-center gap-2"><Icon className="w-4 h-4 text-emerald-500" /><span>{b.text}</span></div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="relative">
            <div className="relative aspect-[4/5] max-w-md mx-auto rounded-[2.5rem] overflow-hidden shadow-2xl shadow-sky-200/60 border-4 border-white">
              {h.image && <img src={resolveImage(h.image)} alt="Hero" className="w-full h-full object-cover" />}
              <div className="absolute inset-0 bg-gradient-to-t from-sky-900/40 via-transparent to-transparent"></div>

              <div className="absolute top-6 left-6 right-6 flex justify-between">
                {h.floatStat1 && (
                  <div className="glass rounded-2xl px-4 py-3 shadow-lg">
                    <p className="text-[10px] uppercase font-bold text-sky-700 tracking-wider">{h.floatStat1.label}</p>
                    <p className="font-display text-2xl font-extrabold text-sky-900 leading-none">{h.floatStat1.value}</p>
                    <p className="text-[10px] text-slate-600">{h.floatStat1.sub}</p>
                  </div>
                )}
                {h.floatStat2 && (
                  <div className="glass rounded-2xl px-4 py-3 shadow-lg">
                    <p className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider">{h.floatStat2.label}</p>
                    <p className="font-display text-2xl font-extrabold text-emerald-900 leading-none">{h.floatStat2.value}</p>
                    <p className="text-[10px] text-slate-600">{h.floatStat2.sub}</p>
                  </div>
                )}
              </div>

              {(h.imageCaption1 || h.imageCaption2) && (
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="glass rounded-2xl px-5 py-4 shadow-xl">
                    {h.imageCaption1 && <p className="text-xs font-semibold text-sky-700 uppercase tracking-wider mb-1">{h.imageCaption1}</p>}
                    {h.imageCaption2 && <p className="font-display font-bold text-sky-950 leading-tight">{h.imageCaption2}</p>}
                  </div>
                </div>
              )}
            </div>

            {(h.accentLabel || h.accentValue) && (
              <div className="hidden lg:block absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-sky-100 p-4 w-56">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-100 to-sky-100 flex items-center justify-center">
                    <Leaf className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">{h.accentLabel}</p>
                    <p className="font-display font-extrabold text-xl text-sky-900">{h.accentValue}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-none pointer-events-none">
        <svg viewBox="0 0 1440 90" className="w-full h-16 md:h-20 text-white" preserveAspectRatio="none">
          <path fill="currentColor" d="M0,32L60,42.7C120,53,240,75,360,69.3C480,64,600,32,720,26.7C840,21,960,43,1080,48C1200,53,1320,43,1380,37.3L1440,32L1440,90L0,90Z" />
        </svg>
      </div>
    </section>
  );
}
